import { css } from 'aphrodite';
import React, { useMemo, useState } from 'react';

import { orgStyles as s } from '@/features/org/components/org.styles';
import { useOrgs } from '@/features/org/hooks/use-orgs/useOrgs';
import { useSlugCheck } from '@/features/org/hooks/use-slug-check/useSlugCheck';
import { useHistory } from '@/shared/hooks/use-router/react-router-hooks';

/**
 * Modal: create a new org. Caller controls visibility; the modal
 * dismisses itself on success and navigates to /o/:slug/dashboard so
 * the user lands in the new context.
 *
 * Slug uniqueness is gated TWO ways:
 *   1. Live `/api/orgs/check-slug` lookup via useSlugCheck — disables
 *      the submit button when unavailable, shows inline status.
 *   2. Server-side: even if the live check passes, createOrg returns
 *      409 on the unique constraint violation. We surface that too.
 *
 * Slug auto-derives from the name as you type. The user can override
 * by typing into the slug field directly; once they do, we stop
 * overwriting it.
 */
function deriveSlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

export default function CreateOrgModal({ onClose }: { onClose: () => void }) {
  const { createOrg } = useOrgs();
  const history = useHistory();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  /** True once the user has typed in the slug input directly; from then
   *  on we stop auto-deriving from name. */
  const [slugTouched, setSlugTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const effectiveSlug = slugTouched ? slug : deriveSlug(name);
  const slugCheck = useSlugCheck(effectiveSlug);

  const canSubmit = useMemo(() => {
    if (!name.trim()) return false;
    if (!effectiveSlug) return false;
    if (slugCheck.status !== 'available') return false;
    return !busy;
  }, [name, effectiveSlug, slugCheck.status, busy]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setErr(null);
    try {
      const org = await createOrg({ name: name.trim(), slug: effectiveSlug });
      onClose();
      history.push(`/o/${org.slug}/dashboard`);
    } catch (e: any) {
      setErr(e.message || 'Failed to create org');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={css(s.modalBackdrop)} onClick={onClose}>
      <div className={css(s.modal)} onClick={(e) => e.stopPropagation()}>
        <div className={css(s.modalTitle)}>Create new org</div>
        <div className={css(s.modalDesc)}>
          Each org has its own apps, members, and billing. You'll be the owner.
        </div>
        {err && <div className={css(s.errorBox)}>{err}</div>}
        <form onSubmit={handleSubmit}>
          <div className={css(s.field)}>
            <label className={css(s.label)}>Name</label>
            <input
              className={css(s.input)}
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Acme Inc"
              required
            />
          </div>
          <div className={css(s.field)}>
            <label className={css(s.label)}>Slug (URL)</label>
            <input
              className={css(s.input, s.inputMono)}
              value={effectiveSlug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value.toLowerCase());
              }}
              placeholder="auto from name"
              pattern="[a-z0-9][a-z0-9-]{1,62}[a-z0-9]"
            />
            <SlugStatusRow slug={effectiveSlug} state={slugCheck} />
          </div>
          <div className={css(s.modalActions)}>
            <button type="button" className={css(s.btnGhost)} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className={css(s.btn)} disabled={!canSubmit}>
              {busy ? 'Creating…' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Small inline row under the slug input. Shape:
 *
 *   /o/<slug>/dashboard      ✓ available  |  ✗ taken  |  · checking…
 *
 * Color coded — red on unavailable, green on available, dim on idle.
 */
function SlugStatusRow({
  slug,
  state,
}: {
  slug: string;
  state: { status: string; message: string | null };
}) {
  if (!slug) {
    return (
      <div style={{ marginTop: 6, fontSize: 11, color: 'var(--td-gray-500, #777)' }}>
        Type a name to auto-pick a URL, or type your own.
      </div>
    );
  }
  let icon: string;
  let color: string;
  let label: string;
  if (state.status === 'checking') {
    icon = '…';
    color = 'var(--td-gray-500, #777)';
    label = 'checking…';
  } else if (state.status === 'available') {
    icon = '✓';
    color = '#1B5E20';
    label = 'available';
  } else if (state.status === 'unavailable') {
    icon = '✗';
    color = '#7A0000';
    label = state.message || 'unavailable';
  } else {
    icon = '·';
    color = 'var(--td-gray-500, #777)';
    label = 'enter a slug';
  }
  return (
    <div
      style={{
        marginTop: 6,
        fontSize: 11,
        color: 'var(--td-gray-500, #777)',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <code style={{ fontFamily: 'var(--mono, monospace)' }}>/o/{slug}/dashboard</code>
      <span style={{ marginLeft: 'auto', color, fontWeight: 600 }}>
        {icon} {label}
      </span>
    </div>
  );
}
